import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react'
import DefaultAuth from 'layouts/auth/Default'
import { MdOutlineRemoveRedEye } from 'react-icons/md'
import { RiEyeCloseLine } from 'react-icons/ri'
import { useAuthStore } from './../../../store/auth.store'
import { useLoginUser } from 'queries/auth.query'
import illustration from 'assets/img/logo/bamyan.png'
import { useFormik } from 'formik'
import * as Yup from 'yup'

const SignInSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
})

function SignIn () {
  const textColor = useColorModeValue('navy.700', 'white')
  const textColorSecondary = 'gray.400'
  const brandStars = useColorModeValue('brand.500', 'brand.400')
  const toast = useToast()

  const { signIn, isLoggedIn, token, user } = useAuthStore()
  const { mutate, isPending } = useLoginUser()
  const [show, setShow] = useState(false)
  const [backendError, setBackendError] = useState('')

  const handleClick = () => setShow(!show)

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: SignInSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: values => {
      setBackendError('')
      mutate(values, {
        onSuccess: response => {
          toast({
            title: 'Welcome Back!',
            description: `Successfully signed in as ${response.user?.username || 'User'}`,
            status: 'success',
            duration: 2500,
            isClosable: true,
            position: 'top',
          })
          signIn({
            isLoggedIn: true,
            token: response.token,
            user: response.user,
          })
        },
        onError: error => {
          console.error('Sign-in error:', error)
          let errorMsg = ''
          const data = error.response?.data

          if (data?.errors && typeof data.errors === 'object') {
            const firstKey = Object.keys(data.errors)[0]
            const firstVal = data.errors[firstKey]
            errorMsg = Array.isArray(firstVal) ? firstVal[0] : String(firstVal)
          } else if (data?.message) {
            errorMsg = data.message
          } else if (data?.error) {
            errorMsg = data.error
          } else if (error.response?.status === 401) {
            errorMsg = 'Invalid email or password. Please verify your credentials.'
          } else if (error.response?.status === 404) {
            errorMsg = 'Backend API endpoint not found (404). Please ensure Laravel backend is running.'
          } else if (error.code === 'ERR_NETWORK' || !error.response) {
            errorMsg = 'Cannot connect to backend server at http://localhost:8000. Please check if Laravel is running.'
          } else {
            errorMsg = error.message || 'Unable to sign in. Please try again.'
          }

          setBackendError(errorMsg)
          toast({
            title: 'Sign In Failed',
            description: errorMsg,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          })
        },
      })
    },
  })

  if (isLoggedIn && token) {
    if (user?.role === 'gate_keeper') {
      return <Navigate to={'/gatekeeper/check-in'} replace />
    }
    return <Navigate to={'/admin/users'} replace />
  }

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w='100%'
        mx={{ base: 'auto', lg: '0px' }}
        me='auto'
        h='100%' 
        alignItems='start'
        justifyContent='start' 
        mt={{ base: '40px', md: '30vh' }} 
        mb={{ base: '30px', md: '0px' }} 
        px={{ base: '25px', md: '0px' }}
        flexDirection='column'
      >
        <Box me='auto'>
          <Heading color={textColor} fontSize='36px' mb='10px'>
            Sign In
          </Heading>
          <Text
            mb='36px'
            ms='4px'
            color={textColorSecondary}
            fontWeight='400'
            fontSize='md'
          >
            Enter your email and password to sign in!
          </Text>
        </Box>
        <Flex
          zIndex='2'
          direction='column'
          w={{ base: '100%', md: '420px' }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: 'auto', lg: 'unset' }}
          me='auto'
          mb={{ base: '20px', md: 'auto' }}
        >
          <form onSubmit={formik.handleSubmit}>
            {backendError && (
              <Alert status='error' borderRadius='lg' mb='16px' py='10px'>
                <AlertIcon />
                <AlertDescription fontSize='sm' fontWeight='500'>
                  {backendError}
                </AlertDescription>
              </Alert>
            )}
            <FormControl
              isInvalid={formik.errors.email && formik.touched.email}
            >
              <FormLabel
                display='flex'
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                mb='10px'
              >
                Email<Text color={brandStars}>*</Text>
              </FormLabel>
              <Input
                variant='auth'
                fontSize='sm'
                type='email'
                placeholder='mail@gmail.com'
                mb='6px'
                fontWeight='500'
                size='lg'
                value={formik.values.email}
                onChange={e => {
                  if (backendError) setBackendError('')
                  formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                name='email'
              />
              {formik.errors.email && formik.touched.email ? (
                <Text color='red.500'>{formik.errors.email}</Text>
              ) : null}
            </FormControl>
            <FormControl
              isInvalid={formik.errors.password && formik.touched.password}
              mt={'3'}
            >
              <FormLabel
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                display='flex'
                mb='10px'
              >
                Password<Text color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup size='md'>
                <Input
                  fontSize='sm'
                  placeholder='Min. 6 characters'
                  mb='10px'
                  size='lg'
                  type={show ? 'text' : 'password'}
                  variant='auth'
                  value={formik.values.password}
                  onChange={e => {
                    if (backendError) setBackendError('')
                    formik.handleChange(e)
                  }}
                  onBlur={formik.handleBlur}
                  name='password'
                />
                <InputRightElement display='flex' alignItems='center' mt='4px'>
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: 'pointer' }}
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClick}
                  />
                </InputRightElement>
              </InputGroup>
              {formik.errors.password && formik.touched.password ? (
                <Text color='red.500' fontSize='sm'>
                  {formik.errors.password}
                </Text>
              ) : null}
            </FormControl>
            <Button
              fontSize='sm'
              variant='brand'
              fontWeight='500'
              w='100%'
              h='50'
              mb='24px'
              mt={'3'}
              type='submit'
              isLoading={isPending}
              isDisabled={isPending}
            >
              Sign In
            </Button>
          </form>
        </Flex>
      </Flex>
    </DefaultAuth>
  )
}

export default SignIn;
